import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import commissionService from '../services/commissionService';
import toast from 'react-hot-toast';
import {
  HiOutlineCurrencyDollar,
  HiOutlineCheckCircle,
  HiOutlineRefresh,
  HiOutlineUser,
  HiOutlineCash,
  HiOutlineCalendar,
  HiOutlineExclamationCircle,
} from 'react-icons/hi';
import './Commissions.css';

const Commissions = () => {
  const { hasRole } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSalesman, setSelectedSalesman] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, paid
  const [confirmModal, setConfirmModal] = useState(null); // { type, id, name, amount }
  const [payingAll, setPayingAll] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (hasRole('admin', 'accountant', 'manager')) {
        const [commRes, salesmenRes] = await Promise.all([
          commissionService.getAll(),
          commissionService.getSalesmen(),
        ]);
        setCommissions(commRes.data || []);
        setSalesmen(salesmenRes.data || []);
      } else if (hasRole('salesman')) {
        const commRes = await commissionService.getMine().catch(() => ({ data: [] }));
        setCommissions(commRes.data || []);
      }
    } catch {
      toast.error('Error cargando comisiones');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  // Marcar UNA comisión como pagada
  const handleMarkPaid = async (id) => {
    try {
      await commissionService.markAsPaid(id);
      toast.success('Comisión marcada como pagada');
      setConfirmModal(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al pagar comisión');
    }
  };

  // Pagar TODAS las comisiones pendientes de un vendedor
  const handlePayAll = async (salesmanId) => {
    setPayingAll(true);
    try {
      const pending = commissions.filter(
        (c) => String(c.salesman_id) === String(salesmanId) && !c.paid
      );
      let paidCount = 0;
      for (const c of pending) {
        try {
          await commissionService.markAsPaid(c.id);
          paidCount++;
        } catch { /* skip already paid */ }
      }
      toast.success(`${paidCount} comisión(es) pagada(s)`);
      setConfirmModal(null);
      fetchData();
    } catch (err) {
      toast.error('Error al pagar comisiones');
    } finally {
      setPayingAll(false);
    }
  };

  // Filtrar comisiones
  let filteredCommissions = selectedSalesman
    ? commissions.filter((c) => String(c.salesman_id) === String(selectedSalesman))
    : commissions;

  if (filterStatus === 'pending') {
    filteredCommissions = filteredCommissions.filter((c) => !c.paid);
  } else if (filterStatus === 'paid') {
    filteredCommissions = filteredCommissions.filter((c) => c.paid);
  }

  // Totales — usando los campos reales del backend
  const totalPending = commissions
    .filter((c) => !c.paid && (!selectedSalesman || String(c.salesman_id) === String(selectedSalesman)))
    .reduce((sum, c) => sum + Number(c.commission_amount || 0), 0);

  const totalPaid = commissions
    .filter((c) => c.paid && (!selectedSalesman || String(c.salesman_id) === String(selectedSalesman)))
    .reduce((sum, c) => sum + Number(c.commission_amount || 0), 0);

  const pendingCount = commissions
    .filter((c) => !c.paid && (!selectedSalesman || String(c.salesman_id) === String(selectedSalesman)))
    .length;

  // Nombre del vendedor seleccionado
  const selectedSalesmanName = salesmen.find((s) => String(s.id) === String(selectedSalesman))?.full_name || '';

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="commissions-page">
      <div className="page-header">
        <div>
          <h1>Comisiones</h1>
          <p>Seguimiento y pago de comisiones de vendedores</p>
        </div>
        <div className="header-actions">
          {/* Botón pagar todas — solo si hay un vendedor seleccionado con pendientes */}
          {hasRole('admin', 'accountant') && selectedSalesman && pendingCount > 0 && (
            <button
              className="btn-pay-all"
              onClick={() => setConfirmModal({
                type: 'payAll',
                id: selectedSalesman,
                name: selectedSalesmanName,
                amount: totalPending,
                count: pendingCount,
              })}
            >
              <HiOutlineCash /> Pagar todas ({pendingCount})
            </button>
          )}
          <button className="btn-icon" onClick={fetchData} title="Actualizar">
            <HiOutlineRefresh />
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="commission-summary-grid">
        <div className="commission-summary-card pending">
          <span className="summary-label">Pendientes ({pendingCount})</span>
          <span className="summary-value">
            RD$ {totalPending.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
          </span>
        </div>
        <div className="commission-summary-card paid">
          <span className="summary-label">Pagadas</span>
          <span className="summary-value">
            RD$ {totalPaid.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
          </span>
        </div>
        <div className="commission-summary-card total">
          <span className="summary-label">Total Generado</span>
          <span className="summary-value">
            RD$ {(totalPending + totalPaid).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      {/* Filtros */}
      {hasRole('admin', 'accountant', 'manager') && (
        <div className="filters-bar">
          {salesmen.length > 0 && (
            <div className="filter-group">
              <HiOutlineUser />
              <select
                value={selectedSalesman}
                onChange={(e) => setSelectedSalesman(e.target.value)}
              >
                <option value="">Todos los vendedores</option>
                {salesmen.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.full_name || s.name} ({Number(s.commission_percentage || 0).toFixed(0)}%)
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="filter-group">
            <HiOutlineCalendar />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="paid">Pagadas</option>
            </select>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-state"><div className="loader"></div><p>Cargando comisiones...</p></div>
      ) : filteredCommissions.length === 0 ? (
        <div className="empty-state-card">
          <HiOutlineCurrencyDollar className="empty-icon" />
          <p>No hay comisiones {filterStatus === 'pending' ? 'pendientes' : filterStatus === 'paid' ? 'pagadas' : 'registradas'}</p>
        </div>
      ) : (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Vendedor</th>
                <th>Orden</th>
                <th>Comisión</th>
                <th>Tasa</th>
                <th>Fecha</th>
                <th>Estado</th>
                {hasRole('admin', 'accountant') && <th>Acción</th>}
              </tr>
            </thead>
            <tbody>
              {filteredCommissions.map((commission) => (
                <tr key={commission.id} className={!commission.paid ? 'row-pending' : ''}>
                  <td>{commission.salesman_name || '-'}</td>
                  <td className="mono-cell">{commission.order_number || `#${commission.order_id || '-'}`}</td>
                  <td className="mono-cell total-cell">
                    RD$ {Number(commission.commission_amount || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </td>
                  <td>{commission.commission_percentage ? `${Number(commission.commission_percentage).toFixed(1)}%` : '-'}</td>
                  <td className="date-cell">
                    {commission.paid
                      ? <span className="date-paid">{formatDate(commission.paid_date)}</span>
                      : <span className="date-created">{formatDate(commission.created_at)}</span>
                    }
                  </td>
                  <td>
                    <span className={`status-badge ${commission.paid ? 'paid-badge' : 'pending-badge'}`}>
                      {commission.paid ? 'Pagada' : 'Pendiente'}
                    </span>
                  </td>
                  {hasRole('admin', 'accountant') && (
                    <td>
                      {!commission.paid && (
                        <button
                          className="btn-pay"
                          onClick={() => setConfirmModal({
                            type: 'single',
                            id: commission.id,
                            name: commission.salesman_name,
                            amount: commission.commission_amount,
                          })}
                        >
                          <HiOutlineCheckCircle /> Pagar
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de confirmación de pago */}
      {confirmModal && (
        <div className="modal-overlay" onClick={() => setConfirmModal(null)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <HiOutlineExclamationCircle />
            </div>
            <h3>Confirmar Pago</h3>
            {confirmModal.type === 'single' ? (
              <>
                <p>¿Marcar como pagada la comisión de <strong>{confirmModal.name}</strong>?</p>
                <div className="confirm-amount">
                  RD$ {Number(confirmModal.amount || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </div>
              </>
            ) : (
              <>
                <p>¿Pagar <strong>todas las comisiones pendientes</strong> de <strong>{confirmModal.name}</strong>?</p>
                <div className="confirm-details">
                  <span>{confirmModal.count} comisión(es)</span>
                  <div className="confirm-amount">
                    RD$ {Number(confirmModal.amount || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </>
            )}
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setConfirmModal(null)}>Cancelar</button>
              <button
                className="btn-confirm-pay"
                disabled={payingAll}
                onClick={() => {
                  if (confirmModal.type === 'single') {
                    handleMarkPaid(confirmModal.id);
                  } else {
                    handlePayAll(confirmModal.id);
                  }
                }}
              >
                <HiOutlineCheckCircle />
                {payingAll ? 'Procesando...' : 'Confirmar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Commissions;
